import { useState } from 'react';

import type { ValidationResult } from '@dcc-erp/shared';

// VALIDATION_FIELD_MAP is defined ONCE in ./field-loc.ts (Task P1.3) alongside
// the field-key scheme. Import it here and re-export for the test/route — do NOT
// redefine it (a second copy would drift from the field-loc source of truth).
import { VALIDATION_FIELD_MAP } from './field-loc';

export { VALIDATION_FIELD_MAP };

type Check = ValidationResult['checks'][number];
type Severity = Check['severity'];
const RANK: Record<Severity, number> = { critical: 0, warning: 1, info: 2 };
const SEV_BADGE: Record<Severity, string> = {
  critical: 'badge negative',
  warning: 'badge warning',
  info: 'badge info',
};
const SEV_LABEL: Record<Severity, string> = {
  critical: 'ร้ายแรง',
  warning: 'เตือน',
  info: 'ข้อมูล',
};

export interface ValidationSummaryProps {
  validation: ValidationResult;
  /** Focus + highlight the field for a clicked entry. */
  onJump: (fieldKey: string) => void;
}

export function ValidationSummary({ validation, onJump }: ValidationSummaryProps) {
  const [filter, setFilter] = useState<Severity | 'all'>('all');
  const failed = validation.checks
    .filter((c) => !c.passed)
    .sort((a, b) => RANK[a.severity] - RANK[b.severity]);
  const visible = filter === 'all' ? failed : failed.filter((c) => c.severity === filter);

  return (
    <div
      role="alert"
      tabIndex={-1}
      className="card"
      style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}
    >
      {failed.length === 0 ? (
        <div className="badge positive">✓ ผ่านการตรวจสอบ</div>
      ) : (
        <>
          {validation.hasCritical && (
            <div className="conf-field__msg" style={{ marginBottom: 8 }}>
              ⚠ แก้ไขข้อผิดพลาดร้ายแรงก่อนบันทึก (
              {failed.filter((c) => c.severity === 'critical').length} รายการ)
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            {(['all', 'critical', 'warning', 'info'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`btn sm ${filter === s ? 'primary' : 'ghost'}`}
                onClick={() => setFilter(s)}
              >
                {s === 'all' ? 'ทั้งหมด' : SEV_LABEL[s]}
              </button>
            ))}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
            {visible.map((c) => {
              const fieldKey = VALIDATION_FIELD_MAP[c.code];
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    className="btn ghost"
                    style={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left' }}
                    disabled={!fieldKey}
                    onClick={() => fieldKey && onJump(fieldKey)}
                  >
                    <span className={SEV_BADGE[c.severity]} style={{ marginRight: 8 }}>
                      {SEV_LABEL[c.severity]}
                    </span>
                    {c.message}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
